import { get, post } from "./client";

/**
 * Client for the contact slug backfill endpoints. The Address Book derives a
 * URL slug for contacts on create, but contacts created before the derivation
 * existed have NULL slugs. The scan previews the slug the generator would
 * produce for each of them; apply writes the slugs for an approved selection
 * of contacts.
 *
 * Both endpoints live on address-book and ride the staff-bff discovery proxy,
 * same as its Developer-section sibling, Office Slug Backfill.
 */

/**
 * Contact ids per `apply` request. Callers with a bigger batch must split it
 * and send the chunks one after another — the response is the only record of
 * which slug each contact actually received, so a single oversized request
 * that timed out would lose the outcome for the whole run.
 */
export const APPLY_CHUNK_SIZE = 100;

export type SkipReason = "unslugifiable";

/**
 * One slugless contact. Exactly one of `plannedSlug` / `skipReason` is
 * non-null: either the generator can plan a slug, or the reason it cannot.
 * Rows come ordered by name.
 */
export type ScanRow = {
  contactId: string;
  name: string;
  plannedSlug: string | null;
  skipReason: SkipReason | null;
};

export type ScanCounts = {
  plannable: number;
  skippedUnslugifiable: number;
};

export type ScanResponse = {
  rows: ScanRow[];
  counts: ScanCounts;
  requestId: string;
};

export type ApplyRowStatus =
  | "generated"
  | "skipped_not_empty"
  | "skipped_unslugifiable"
  | "not_found";

/** One result per submitted contact id. */
export type ApplyRowResult = {
  contactId: string;
  name: string | null;
  status: ApplyRowStatus;
  /** The slug that was written. Present only on status "generated". */
  slug?: string;
};

export type ApplyCounts = {
  generated: number;
  skipped: number;
  notFound: number;
};

export type ApplyResponse = {
  results: ApplyRowResult[];
  counts: ApplyCounts;
  requestId: string;
};

export const contactSlugBackfillService = {
  async scan(): Promise<ScanResponse> {
    return get<ScanResponse>("/v1/address-book/contact-slug-backfill");
  },

  async apply(contactIds: string[]): Promise<ApplyResponse> {
    return post<ApplyResponse>("/v1/address-book/contact-slug-backfill/apply", { contactIds });
  },
};
